import React from 'react';
import {
  MoreInfoOutlined,
  SubtractUniversal,
  WarningAltOutlined,
} from '@duxton-web/icons';
import { Select, Tag, Tooltip, Typography } from '@duxton-web/ui';

import { Metric, MetricWithDimensionFields } from './types';
import {
  formatUTCStringToDateTimeString,
  getTimezoneDisplay,
} from '@/utils/dateTime';

const { Option } = Select;
const { Text } = Typography;

export const getOptions = (data: Metric[], targetMetrics: Metric[]): any[] => {
  const finalData = data?.length > 0 ? data : targetMetrics;
  return finalData?.map((metric: Metric) => {
    return (
      <Option
        key={metric.metadata?.display_name || metric.name}
        label={
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <Text>{metric?.metadata?.display_name || metric.name}</Text>
            {metric?.version && (
              <Tag size="xsmall" style={{ height: 'fit-content' }}>
                {`v${metric.version}`}
              </Tag>
            )}
            {metric?.is_local_timezone && (
              <Tooltip
                title={
                  <>
                    Results on the experiment&apos;s first and last day may be
                    slightly inaccurate because its a local timezone metric.{' '}
                    <a
                      href="https://docs.google.com/document/d/155iAwQKnvqCHpZE2ScsLU1oukepQrHt0xHaBq7pvgPM/edit?tab=t.0#bookmark=id.eiutk2a98vgc"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Know More
                    </a>
                  </>
                }
              >
                <WarningAltOutlined
                  style={{
                    color: '#faad14',
                    cursor: 'help',
                  }}
                />
              </Tooltip>
            )}
          </div>
        }
        value={metric.name}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
          }}
        >
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <Text>{metric?.metadata?.display_name}</Text>
              {metric?.version && (
                <Tag size="xsmall" style={{ height: 'fit-content' }}>
                  {`v${metric.version}`}
                </Tag>
              )}
              {metric?.is_local_timezone && (
                <Tooltip
                  title={
                    <>
                      Results on the experiment&apos;s first and last day may be
                      slightly inaccurate because its a local timezone metric.{' '}
                      <a
                        href="https://docs.google.com/document/d/155iAwQKnvqCHpZE2ScsLU1oukepQrHt0xHaBq7pvgPM/edit?tab=t.0#bookmark=id.eiutk2a98vgc"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Know More
                      </a>
                    </>
                  }
                >
                  <WarningAltOutlined
                    style={{
                      color: '#faad14',
                      cursor: 'help',
                    }}
                  />
                </Tooltip>
              )}
            </div>
            <Tooltip
              color="#FFF"
              zIndex={9999}
              overlayStyle={{ minWidth: '320px' }}
              title={
                <div style={{ padding: '12px 8px 8px 8px' }}>
                  <div
                    style={{
                      display: 'flex',
                      gap: 8,
                      flexDirection: 'column',
                    }}
                  >
                    <div
                      style={{
                        width: '100%',
                        padding: 8,
                        background: '#FFF4EB',
                        border: '1px solid #F09800',
                        borderRadius: 8,
                      }}
                    >
                      <Text style={{ color: '#000', fontSize: 12 }}>
                        This metric was last refreshed on{' '}
                        {formatUTCStringToDateTimeString(
                          metric?.metric_quality?.last_refresh_date,
                        )}{' '}
                      </Text>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {getTimezoneDisplay()}
                      </Text>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <Text style={{ fontSize: 16 }} strong>
                        {metric?.metadata?.display_name}
                      </Text>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <Text strong style={{ fontSize: 12 }} type="secondary">
                        DESCRIPTION
                      </Text>
                      <Text style={{ color: '#000', fontSize: 12 }}>
                        {metric.description || '-'}
                      </Text>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <Text strong style={{ fontSize: 12 }} type="secondary">
                        OWNER
                      </Text>
                      {metric.owners?.length ? (
                        metric.owners.map((owner) => (
                          <Text key={owner} style={{ fontSize: 12 }}>
                            @{owner}
                          </Text>
                        ))
                      ) : (
                        <Text style={{ fontSize: 12 }} type="secondary">
                          -
                        </Text>
                      )}
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <Text strong style={{ fontSize: 12 }} type="secondary">
                        TABLE
                      </Text>
                      {metric.sources?.length ? (
                        metric.sources.map((s) => (
                          <Text key={s} style={{ fontSize: 12 }}>
                            {s}
                          </Text>
                        ))
                      ) : (
                        <Text style={{ fontSize: 12 }} type="secondary">
                          -
                        </Text>
                      )}
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <Text strong style={{ fontSize: 12 }} type="secondary">
                        NUMERATOR
                      </Text>
                      <Text style={{ color: '#000', fontSize: 12 }}>
                        {metric?.expr || '-'}
                      </Text>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <Text strong style={{ fontSize: 12 }} type="secondary">
                        WHERE
                      </Text>
                      <SubtractUniversal style={{ color: '#000' }} />
                    </div>
                  </div>
                </div>
              }
            >
              <MoreInfoOutlined style={{ color: '#4096ff' }} />
            </Tooltip>
            <br />
            <div style={{ display: 'flex', alignItems: 'top' }}>
              {metric.owners?.length &&
                metric.owners.map((owner) => (
                  <>
                    <Text key={owner} style={{ fontSize: 12 }}>
                      @{owner}
                    </Text>
                    <span style={{ margin: '0 2px' }} />
                  </>
                ))}

              {metric.description && (
                <Text
                  style={{ whiteSpace: 'normal', fontSize: 12 }}
                  type="secondary"
                >
                  {metric.description}
                </Text>
              )}
            </div>
          </div>
          {!!metric?.analysis_dimension && (
            <Tag color="green" size="xsmall" style={{ height: 'fit-content' }}>
              {`AU = ${metric?.analysis_dimension}`}
            </Tag>
          )}
        </div>
      </Option>
    );
  });
};

export const BLACKLISTED_GUARDRAIL_METRICS = [
  {
    name: 'lrpar_certified_rrpar_transport_btr',
    description:
      '% of transport sessions which have a booking created (transport sessions is those ever entered ANY of the transport flow.)\nCertified by the transport team.',
    owners: ['alakananda.giridhar'],
    type: 'ratio',
    dimensions: ['passenger_id', 'pax_exp_variant_order', 'country', 'city'],
    sources: ['ocd_certified.transport_f_funnel_session_bookingcode_v2'],
    platform: 'asi_backend',
    query_engine: 'asi_metric_platform',
    metadata: {
      display_name: '(Certified) Transport Btr',
      tags: ['is_distinct', 'count_distinct_metric'],
      vertical: 'All',
    },
    expr: 'COUNT(DISTINCT IF((mobility_home > 0 OR mobility_confirmation > 0) AND mobility_book > 0, analytics_session_id, NULL)) / COUNT(DISTINCT IF(mobility_home > 0 OR mobility_confirmation > 0, analytics_session_id, NULL))',
    is_higher_better: 1,
    data_sensitivity: 'Others',
    metric_quality: {
      last_refresh_date: '2023-12-14T07:13:27.961Z',
    },
    version: 1,
  },
  {
    name: 'food_session_conversion_rate_lrpar_2024_rrpar',
    description:
      'Food Session Conversion Rate (2024) - based on scribe_session_id',
    owners: ['jeremy.thia'],
    type: 'ratio',
    dimensions: ['passenger_id', 'pax_exp_variant_order', 'country', 'city'],
    sources: ['analytics_food.jt_food_daily_session_funnel_v2'],
    platform: 'asi_backend',
    query_engine: 'asi_metric_platform',
    metadata: {
      display_name: 'Food Session Conversion Rate (2024)',
      tags: ['is_distinct', 'count_distinct_metric'],
      vertical: 'All',
    },
    expr: 'COUNT(DISTINCT(CASE WHEN checkout > 0 THEN analytics_session_id END)) / COUNT(DISTINCT(CASE WHEN pre_order_funnel_session > 0 THEN analytics_session_id END))',
    is_higher_better: 1,
    data_sensitivity: 'Others',
    metric_quality: {
      last_refresh_date: '2023-12-14T08:26:03.215Z',
    },
    version: 1,
  },
];

export const constructDimensionFields = (
  metric,
): MetricWithDimensionFields => ({
  ...metric,
  dimension_filters: [
    {
      dimension_filter_constraints: [],
    },
  ],
  join_strategy: metric?.join_strategy,
  metric_default_value:
    metric?.join_strategy && metric?.metric_default_value
      ? metric?.metric_default_value ?? 0
      : undefined,
});

export const normalizeMetricDataFromServer = (
  metric: Partial<MetricWithDimensionFields>,
): MetricWithDimensionFields | null => {
  if (!metric.name) return null;

  return {
    ...(metric as Metric),
    dimension_filters: [
      {
        dimension_filter_constraints:
          metric.dimension_filters?.[0]?.dimension_filter_constraints || [],
      },
    ],
  };
};

/**
 * Combines filtered dimension values with full dimension values to ensure
 * selected values always have their display labels available.
 *
 * @param filteredDimensionValues - Dimension values filtered by population filters (for dropdown options)
 * @param fullDimensionValues - Complete list of dimension values (for preserving labels)
 * @param selectedValues - Currently selected values that need labels preserved
 * @returns Combined array of dimension values with labels
 */
export const combineDimensionValues = (
  filteredDimensionValues: {
    value: string | boolean | number;
    display_value: string;
  }[],
  fullDimensionValues: {
    value: string | boolean | number;
    display_value: string;
  }[],
  selectedValues:
    | (string | boolean | number)[]
    | string
    | boolean
    | number
    | null
    | undefined,
): { value: string | boolean | number; display_value: string }[] => {
  const valueMap = new Map();

  // Add all filtered dimension values (for dropdown options)
  filteredDimensionValues.forEach((dv) => {
    valueMap.set(dv.value, dv);
  });

  // Add any selected values from full list that aren't in filtered results
  if (selectedValues && fullDimensionValues.length > 0) {
    const normalizedSelectedValues = Array.isArray(selectedValues)
      ? selectedValues
      : [selectedValues];

    normalizedSelectedValues.forEach((selectedValue) => {
      if (!valueMap.has(selectedValue)) {
        const fullValue = fullDimensionValues.find(
          (dv) => dv.value === selectedValue,
        );
        if (fullValue) {
          valueMap.set(selectedValue, fullValue);
        }
      }
    });
  }

  return Array.from(valueMap.values());
};
